import { Alert, AlertButton, AlertOptions, Platform } from 'react-native';

import { getUserErrorMessage, isRetryableError } from '@helpers/Errors';
import { logError } from '@helpers/Loggers';

const DEFAULT_ERROR_MESSAGE = 'Something went wrong. Please try again.';

export type ErrorAlertOptions = {
  /** Label for the optional recovery action. Defaults to "Try Again". */
  actionLabel?: string;
  /** Optional recovery action shown only for retryable errors. */
  onAction?: () => Promise<unknown> | unknown;
};

export function alert(
  title: string,
  message: string,
  buttons?: AlertButton[],
  options?: AlertOptions
) {
  if (Platform.OS === 'web') {
    alertWeb(title, message, buttons);
  } else {
    Alert.alert(title, message, buttons, options);
  }
}

export function errorAlert(error?: unknown, options: ErrorAlertOptions = {}) {
  alert(
    'Error',
    error ? getUserErrorMessage(error) : DEFAULT_ERROR_MESSAGE,
    getActionButtons(error, options)
  );
}

function getActionButtons(error: unknown | undefined, options: ErrorAlertOptions) {
  const onAction = options.onAction;
  if (!onAction) return undefined;
  if (error !== undefined && !isRetryableError(error)) return undefined;

  return [
    { text: 'Cancel', style: 'cancel' },
    {
      text: options.actionLabel || 'Try Again',
      onPress: () => callActionSafely(onAction),
    },
  ] satisfies AlertButton[];
}

function callActionSafely(onAction: NonNullable<ErrorAlertOptions['onAction']>) {
  try {
    void Promise.resolve(onAction()).catch(logError);
  } catch (error) {
    logError(error);
  }
}

function alertWeb(title: string, message: string, buttons?: AlertButton[]) {
  const actionButton = buttons?.find((button) => button.style !== 'cancel');
  if (!actionButton) {
    window.alert(title + '\n' + message);
    return;
  }

  if (window.confirm(title + '\n' + message)) {
    actionButton.onPress?.();
  } else {
    buttons?.find((button) => button.style === 'cancel')?.onPress?.();
  }
}
